import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Brain, 
  Shield, 
  Users, 
  Award, 
  Target, 
  Zap, 
  Lock,
  Eye,
  CheckCircle,
  ArrowRight,
  Star,
  TrendingUp,
  Globe,
  Smartphone,
  Headphones,
  BookOpen,
  BarChart3,
  FileText,
  User,
  ArrowRightLeft,
  Menu,
  X
} from 'lucide-react';
import SabiwabeeLogo from '../components/SabiwabeeLogo';

const About = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const values = [
    {
      icon: Heart,
      title: "Compassion First",
      description: "Every interaction is guided by genuine care and understanding for our users' mental health journey.",
      color: "text-red-500"
    },
    {
      icon: Shield,
      title: "Privacy & Security",
      description: "Your mental health data is protected with enterprise-grade security and complete privacy.",
      color: "text-blue-500"
    },
    {
      icon: Brain,
      title: "Innovation",
      description: "We continuously advance AI technology to provide better, more personalized mental health support.",
      color: "text-purple-500"
    },
    {
      icon: Eye,
      title: "Transparency",
      description: "We believe in complete openness about how our AI works and makes decisions.",
      color: "text-green-500"
    },
    {
      icon: Users,
      title: "Community",
      description: "Building supportive communities where no one feels alone in their mental health journey.",
      color: "text-pink-500"
    },
    {
      icon: Target,
      title: "Accessibility",
      description: "Making mental health support available to everyone, regardless of location or financial means.",
      color: "text-orange-500"
    }
  ];



  const milestones = [
    {
      year: "2024-2026",
      title: "Platform Development",
      description: "Building Sabiwabee with AI-powered therapy and comprehensive mental health tools."
    },
    {
      year: "2024-2026",
      title: "Early Access",
      description: "Launching early access program to gather feedback and improve our platform."
    },
    {
      year: "2024-2026",
      title: "Community Building",
      description: "Establishing partnerships with mental health professionals and building our community."
    },
    {
      year: "2024-2026",
      title: "Research & Validation",
      description: "Conducting research studies to validate our approach and improve outcomes."
    }
  ];

  const goals = [
    {
      number: "10,000+",
      label: "Users to Support",
      icon: Users
    },
    {
      number: "50,000+",
      label: "Therapy Sessions",
      icon: Brain
    },
    {
      number: "95%",
      label: "Target Satisfaction",
      icon: Star
    },
    {
      number: "24/7",
      label: "Global Availability",
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                <SabiwabeeLogo className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">
                Sabiwabee
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors">Home</Link>
              <a href="#mission" className="text-gray-600 hover:text-blue-600 transition-colors">Mission</a>
              <a href="#technology" className="text-gray-600 hover:text-blue-600 transition-colors">Technology</a>
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">Sign In</Link>
              <Link to="/register" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg">Get Started</Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-2 space-y-1">
              <Link to="/" className="block px-3 py-2 text-gray-600 hover:text-blue-600">Home</Link>
              <a href="#mission" className="block px-3 py-2 text-gray-600 hover:text-blue-600">Mission</a>
              <a href="#technology" className="block px-3 py-2 text-gray-600 hover:text-blue-600">Technology</a>
              <Link to="/login" className="block px-3 py-2 text-blue-600 font-medium">Sign In</Link>
              <Link to="/register" className="block px-3 py-2 bg-blue-600 text-white rounded-lg text-center">Get Started</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Sabiwabee
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              We're on a mission to make mental health support accessible, effective, and available to everyone. 
              Through innovative AI technology and human-centered design, we're transforming how people access 
              mental health care.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Mental health is fundamental to human well-being, yet millions of people struggle to access 
                the support they need. Traditional barriers like cost, location, stigma, and limited availability 
                prevent people from getting help when they need it most.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Sabiwabee was founded to break down these barriers. We believe everyone deserves access to 
                high-quality mental health support, regardless of their circumstances. Our AI-powered platform 
                provides 24/7 access to therapeutic support, making mental health care more accessible, 
                affordable, and effective than ever before.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg inline-flex items-center justify-center"
                >
                  Join Our Mission
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <a 
                  href="#technology" 
                  className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-200 inline-flex items-center justify-center"
                >
                  Learn About Our Technology
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-8 rounded-2xl">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Accessibility</h3>
                      <p className="text-gray-600">Available 24/7, anywhere in the world</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Privacy</h3>
                      <p className="text-gray-600">Enterprise-grade security and complete privacy</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Innovation</h3>
                      <p className="text-gray-600">Cutting-edge AI technology for personalized care</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do, from product development to user support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${value.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Goals Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Goals
            </h2>
            <p className="text-xl text-gray-600">
              Ambitious targets to transform mental health care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {goals.map((goal, index) => {
              const Icon = goal.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{goal.number}</div>
                  <div className="text-gray-600">{goal.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>



      {/* Technology Section */}
      <section id="technology" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Technology
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Cutting-edge AI technology designed specifically for mental health support
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced AI Models</h3>
                    <p className="text-gray-600">Our AI is trained on extensive mental health data and therapeutic techniques, providing personalized, evidence-based support.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Transparent AI</h3>
                    <p className="text-gray-600">We believe in complete transparency about how our AI works and makes decisions, building trust with our users.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise Security</h3>
                    <p className="text-gray-600">Your data is protected with enterprise-grade security, encryption, and privacy controls that exceed healthcare standards.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-time Processing</h3>
                    <p className="text-gray-600">Our platform processes conversations in real-time, providing immediate support and crisis detection when needed.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-8 rounded-2xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">AI Response Time</span>
                    <span className="text-green-600 font-semibold">~2 seconds</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Uptime</span>
                    <span className="text-green-600 font-semibold">99.9%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Data Encryption</span>
                    <span className="text-green-600 font-semibold">AES-256</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Privacy Compliance</span>
                    <span className="text-green-600 font-semibold">HIPAA Ready</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Roadmap
            </h2>
            <p className="text-xl text-gray-600">
              Our planned journey to transform mental health care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 text-center">
                <div className="text-2xl font-bold text-blue-600 mb-2">{milestone.year}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                <p className="text-gray-600">{milestone.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join Us in Transforming Mental Health Care
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Be part of the movement to make mental health support accessible to everyone. 
            Start your journey with Sabiwabee today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link 
              to="/" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200 flex items-center justify-center"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                  <SabiwabeeLogo className="w-4 h-4 text-white" />
                </div>
                <span className="text-xl font-bold text-white">Sabiwabee</span>
              </div>
              <p className="text-gray-400">
                Transforming mental health care through innovative AI technology and human-centered design.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#mission" className="hover:text-white transition-colors">Our Mission</a></li>
                <li><a href="#technology" className="hover:text-white transition-colors">Technology</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Get Started</h3>
              <div className="space-y-2">
                <Link 
                  to="/register" 
                  className="block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-center hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                >
                  Sign Up Free
                </Link>
                <Link 
                  to="/login" 
                  className="block border border-gray-600 text-white px-4 py-2 rounded-lg text-center hover:bg-gray-800 transition-all duration-200"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Sabiwabee. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About; 